/*
 * The one-time credential dialog.
 *
 * *** IT MUST FIT ON A LAPTOP SCREEN ***
 * Everything here competes for height, and this dialog previously ran past the
 * bottom of the viewport with its primary button below the fold. So the layout
 * is deliberately tight, the action lives in the modal's sticky footer, and the
 * only element given room to breathe is the password itself.
 */
.body {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

/* ------------------------------------------------------------- password */
/* Boxed and tinted so the irrecoverable value is the first thing seen. */
.passwordBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  background: var(--indigo-soft);
  border: 1px solid var(--indigo);
  border-radius: var(--radius);
}

.passwordValue {
  flex: 1;
  min-width: 0;
  padding: var(--space-3);
  /* Monospace so a generated password is unambiguous — someone reading this
     aloud or retyping it must not confuse l/1/I or O/0. */
  font-family: var(--font-mono);
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
  letter-spacing: 0.08em;
  color: var(--indigo);
  background: var(--bg);
  border: 1px solid var(--indigo);
  border-radius: var(--radius);
  user-select: all;
}

.warning {
  margin: 0;
  font-size: var(--text-xs);
  line-height: var(--leading-normal);
  color: var(--text-muted);
}

/* ---------------------------------------------------------------- email */
.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.fieldLabel {
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
}

.fieldRow {
  display: flex;
  gap: var(--space-2);
  align-items: stretch;
}

.fieldValue {
  flex: 1;
  min-width: 0;
  padding: var(--space-2) var(--space-3);
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  color: var(--text);
  background: var(--bg-sunken);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  user-select: all;
}

/* A quiet third option: the two Copy buttons above are the primary route, so
   this must not compete with them for attention. */
.copyBoth {
  align-self: flex-start;
  padding: 0;
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--indigo);
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.copyBoth:hover { opacity: 0.75; }

/* ----------------------------------------------------------- next steps */
/* Reference material, not an instruction to follow now — so it is small and
   sits below everything actionable. */
.steps {
  margin: 0;
  padding-left: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  font-size: var(--text-xs);
  color: var(--text-muted);
  line-height: var(--leading-normal);
}

.steps li::marker {
  color: var(--indigo);
  font-weight: var(--weight-semibold);
}

.copiedHint {
  margin: 0;
  font-size: var(--text-xs);
  color: var(--success);
}

/* --------------------------------------------------------------- footer */
.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  width: 100%;
}

.acknowledge {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  cursor: pointer;
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
}

.acknowledge input {
  width: 16px;
  height: 16px;
  accent-color: var(--indigo);
  flex-shrink: 0;
  cursor: pointer;
}

/* On a narrow screen the checkbox and button stack rather than squeezing the
   label to a few characters per line. */
@media (max-width: 560px) {
  .footer {
    flex-direction: column;
    align-items: stretch;
  }
}
